import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { IconHighlight } from '@aimsos/ui';

/**
 * One widget-type tile (CT-5196).
 *
 * A real `role="radio"`, not a dimmed div, so the grid reads as one choice.
 *
 * **Disabled, never absent.** A type the chosen data mode cannot build stays on
 * the grid, dimmed and unselectable, with the reason on hover — the whole
 * catalog is what tells an author what the builder can make, and a grid that
 * silently loses two thirds of its tiles reads as a bug rather than a rule.
 *
 * `memo()` because 28 of these render at once; the parent passes one stable
 * `onSelect` and the tile supplies its own id.
 */
const WidgetTypeTile = memo(function WidgetTypeTile({
  typeId,
  icon,
  label,
  selected,
  disabled = false,
  disabledReason,
  onSelect,
}) {
  const handleClick = useCallback(() => {
    if (!disabled) onSelect(typeId);
  }, [disabled, onSelect, typeId]);

  const handleKeyDown = useCallback(
    (event) => {
      if (disabled) return;
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        onSelect(typeId);
      }
    },
    [disabled, onSelect, typeId],
  );

  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      aria-label={label}
      // The reason replaces the name on hover: the label is already on the
      // tile, so repeating it there would waste the one line a title gets.
      title={disabled && disabledReason ? disabledReason : label}
      // Out of the arrow-key ring entirely, so tabbing through the grid never
      // lands on a choice that cannot be made.
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={[
        'flex min-w-0 flex-col items-center gap-1.5 rounded-lg border p-2.5 text-center transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        disabled ? 'cursor-not-allowed border-edge opacity-40' : 'cursor-pointer',
        // eslint-disable-next-line no-nested-ternary
        disabled
          ? ''
          : selected
            ? 'border-brand-500 bg-brand-50/60 dark:bg-brand-950/30'
            : 'border-edge hover:border-brand-300',
      ].join(' ')}
    >
      <IconHighlight
        icon={icon}
        size="small"
        bgColor={selected && !disabled ? 'Primary' : 'Neutral'}
        iconColor={selected && !disabled ? 'PrimaryAlt' : 'Primary'}
      />
      <span className="min-w-0 text-[11px] font-medium leading-tight text-content-secondary">
        {label}
      </span>
    </div>
  );
});

WidgetTypeTile.propTypes = {
  typeId: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  /** The chosen data mode cannot build this type. */
  disabled: PropTypes.bool,
  /** Why, shown on hover. */
  disabledReason: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default WidgetTypeTile;
